import { liteClient as algoliasearch } from "algoliasearch/lite";
import {
  ClearRefinements,
  InfiniteHits,
  InstantSearch,
  SearchBox,
  Stats,
} from "react-instantsearch";
import React from "react";
import { useState } from "@wordpress/element";
import { Hit } from "instantsearch.js";
import ToggleOuiNon from "./Component/ToggleOuiNon";
import { RangeSlider } from "./Component/RangeSlider";
import CustomRefinementList from "./Component/CustomRefinementList";
import SidePanel from "./Component/SidePanel";
import useEstMobile from "./Component/useEstMobile";
import { history } from "instantsearch.js/es/lib/routers";
import { simple } from "instantsearch.js/es/lib/stateMappings";

const searchClient = algoliasearch(
  window.salleVars.ALGOLIA_APP_ID,
  window.salleVars.ALGOLIA_API_SEARCH_KEY
);

const routing = {
  router: history(),
  stateMapping: simple(),
};

function HitResult({ hit }: { hit: Hit<SalleCours.SalleHit> }) {
  const estMobile = useEstMobile();

  return (
    <div>
      <h5>
        <a href={hit?.permalink}>
          <span>
            {hit?.local}
            {estMobile && hit?.type_salle && ` - ${hit?.type_salle}`}
          </span>
          <span>{hit?.capacite ? hit?.capacite : "-"}</span>
          {!estMobile && <span>{hit?.type_salle ? hit?.type_salle : "-"}</span>}
        </a>
      </h5>
    </div>
  );
}

export default function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const estMobile = useEstMobile();

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={window.salleVars.ALGOLIA_INDEX}
      future={{ preserveSharedStateOnUnmount: true }}
      routing={routing}
    >
      <div className="search">
        <SearchBox placeholder="Trouver un local"></SearchBox>
        <button
          className="filtrer"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="16"
            viewBox="0 0 14 16"
            fill="none"
          >
            <path
              d="M11.5332 1.33203V14.6654"
              stroke="black"
              stroke-width="1.2"
              stroke-miterlimit="10"
              stroke-linecap="round"
            />
            <path
              d="M11.5329 6.46615C12.4534 6.46615 13.1995 5.71995 13.1995 4.79948C13.1995 3.879 12.4534 3.13281 11.5329 3.13281C10.6124 3.13281 9.86621 3.879 9.86621 4.79948C9.86621 5.71995 10.6124 6.46615 11.5329 6.46615Z"
              fill="black"
            />
            <path
              d="M2.4668 1.33203V14.6654"
              stroke="black"
              stroke-width="1.2"
              stroke-miterlimit="10"
              stroke-linecap="round"
            />
            <path
              d="M2.46647 6.46615C3.38695 6.46615 4.13314 5.71995 4.13314 4.79948C4.13314 3.879 3.38695 3.13281 2.46647 3.13281C1.546 3.13281 0.799805 3.879 0.799805 4.79948C0.799805 5.71995 1.546 6.46615 2.46647 6.46615Z"
              fill="black"
            />
            <path
              d="M7 14.6654V1.33203"
              stroke="black"
              stroke-width="1.2"
              stroke-miterlimit="10"
              stroke-linecap="round"
            />
            <path
              d="M6.99967 13.5326C7.92015 13.5326 8.66634 12.7864 8.66634 11.8659C8.66634 10.9454 7.92015 10.1992 6.99967 10.1992C6.0792 10.1992 5.33301 10.9454 5.33301 11.8659C5.33301 12.7864 6.0792 13.5326 6.99967 13.5326Z"
              fill="black"
            />
          </svg>
          <span>Filtrer</span>
        </button>
      </div>

      <SidePanel
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        titre=""
        sousTitre="Salle de cours et caractéristiques"
      >
        <RangeSlider title="Capacité" attribute="capacite" />
        <ToggleOuiNon
          attribute="haut_parleur_rappel"
          title="Rappel haut-parleur"
        ></ToggleOuiNon>
        <ToggleOuiNon
          attribute="amplification_voix"
          title="Amplification de la voix"
        ></ToggleOuiNon>
        <ToggleOuiNon
          attribute="micro_sans_fil"
          title="Micro sans fil"
        ></ToggleOuiNon>
        <CustomRefinementList attribute="amenagement" title="Aménagement" />
        <CustomRefinementList attribute="tableau" title="Tableau" />
        <CustomRefinementList
          attribute="toile_projection_grandeur"
          labelsToExclude={["Ne s'applique pas", "À confirmer"]}
          title="Grandeur de la toile de projection"
        />
        <CustomRefinementList
          labelsToExclude={["À confirmer"]}
          attribute="toile_projection_type"
          title="Toile de projection"
        />
        <CustomRefinementList
          attribute="type_affichage"
          labelsToExclude={["À confirmer"]}
          title="Type d'affichage"
        />
        <CustomRefinementList
          attribute="type_de_camera"
          title="Type de caméra"
        />
        <CustomRefinementList
          attribute="type_de_mobilier"
          title="Type de mobilier"
        />
        <CustomRefinementList
          labelsToExclude={["À confirmer"]}
          attribute="type_salle"
          title="Type de salle"
        />
        <CustomRefinementList
          labelsToExclude={["À confirmer"]}
          attribute="typologie_audiovisuel"
          title="Typologie audiovisuelle"
        />
        <ClearRefinements
          translations={{ resetButtonText: "Effacer les filtres" }}
        />
      </SidePanel>

      <div className="hits">
        <div className="stats">
          <Stats
            translations={{
              rootElementText({
                nbHits,
                processingTimeMS,
                nbSortedHits,
                areHitsSorted,
              }) {
                const total = nbHits.toLocaleString("fr-CA");
                const sorted = nbSortedHits?.toLocaleString("fr-CA");

                const isSingular = nbHits === 1;

                if (areHitsSorted && nbHits !== nbSortedHits) {
                  return `${sorted} résultat${
                    nbSortedHits === 1 ? "" : "s"
                  } pertinent${nbSortedHits === 1 ? "" : "s"} trié${
                    nbSortedHits === 1 ? "" : "s"
                  } sur un total de ${total} résultat${isSingular ? "" : "s"}`;
                }

                return `${total} résultat${isSingular ? "" : "s"}`;
              },
            }}
          />
        </div>

        <ol className="ais-InfiniteHits-list">
          <li className="ais-InfiniteHits-item">
            <div>
              <h5>
                <a>
                  <span>Local</span>
                  <span>Capacité</span>
                  {!estMobile && <span>Type de salle</span>}
                </a>
              </h5>
            </div>
          </li>
        </ol>

        <InfiniteHits
          hitComponent={HitResult}
          translations={{ showMoreButtonText: "Voir plus de résultats" }}
          transformItems={(items) =>
            [...items].sort((a, b) => {
              const A = (a.name ?? a.title ?? "").toString();
              const B = (b.name ?? b.title ?? "").toString();
              return A.localeCompare(B, "fr", { sensitivity: "base" });
            })
          }
        />
      </div>
    </InstantSearch>
  );
}
